import Box from '@mui/material/Box';
import SimpleModal, { ScrollableModalContent } from 'common/components/Elements/Modal/SimpleModal/SimpleModal';
import useViewportSize from 'common/utilities/hooks/useViewportSize/useViewportSize';
import type { ApplicationState } from 'core/redux/reducers/rootReducer';
import { SCROLLBAR_WIDTH } from 'dataAnalyzer/components/Chart/useScrollableChart';
import I18n from 'i18n/en-US';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import type { ResizeCallbackData } from 'react-resizable';
import type { Sequence, SequenceAlign } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import type { HandleAlignedSequencesChange } from 'sequenceAnalyser/components/SequenceAnalyser/SequenceAnalyser';
import SimilarityTree, { leafDistance } from 'sequenceAnalyser/components/SimilarityTree/SimilarityTree';
import useSimilarityTreeMenu from 'sequenceAnalyser/components/SimilarityTree/useSimilarityTreeMenu';

interface ComponentProps {
  open: boolean;
  onOpen: (open: boolean) => void;
  sequences: Sequence[];
  alignedSequences: SequenceAlign[];
  onAlignedSequencesChange: HandleAlignedSequencesChange;
}

function SimilarityTreeModal({
  open, onOpen, sequences, alignedSequences, onAlignedSequencesChange,
}: ComponentProps) {
  const sidebarPixelWidth = useSelector((state: ApplicationState) => state.sequenceAnalyser.sidebarPixelWidth);
  const viewportSize = useViewportSize();

  const usedHeightAroundTreeView = 126;
  const availableHeight = viewportSize.height - usedHeightAroundTreeView;
  let height = (alignedSequences.length * leafDistance);
  if (height > availableHeight) height = availableHeight;
  const [size, setSize] = useState<ResizeCallbackData['size']>({
    width: (viewportSize.width * (2 / 3)) - sidebarPixelWidth,
    height,
  });

  const {
    SimilarityTreeMenu, SortBySimilarityLoading, canvasId, showDistanceToggle,
  } = useSimilarityTreeMenu(
    alignedSequences, onAlignedSequencesChange,
  );

  return (
    <SimpleModal
      title={I18n.SequenceAnalyser.TableActions.SimilarityTreeModal.title}
      open={open}
      onClose={() => {
        onOpen(false);
      }}
      draggablePoppover
      defaultPosition={{ x: sidebarPixelWidth, y: 0 }}
      size="auto"
      headerActions={SimilarityTreeMenu}
      content={(
        <Box sx={{ mt: 1, mb: 1 }}>
          <ScrollableModalContent resizable={size} onResize={(newSize) => setSize(newSize)} noActions>
            <SimilarityTree
              sequences={sequences}
              alignedSequences={alignedSequences}
              width={size.width - SCROLLBAR_WIDTH}
              SortBySimilarityLoading={SortBySimilarityLoading}
              canvasId={canvasId}
              showDistanceToggle={showDistanceToggle}
            />
          </ScrollableModalContent>
        </Box>
      )}
    />
  );
}

export default SimilarityTreeModal;
